import { SEARCH_KEYS } from '../../constants/query-params/search';
import { FORMAT_FILTER_IDS, OWNED_BY, PRICE_FILTER_IDS, TOTAL_PAGES_IDS } from '../../constants/search';
import { world } from '../../languages';
import { storageKeys } from '../browser-storage/storage-keys';
import twoDigitsFormat from '../two-digits-format';

const GQLMap = {
    [SEARCH_KEYS.TAXONOMY]: {
        formFilterString(value: SearchState[SEARCH_KEYS.TAXONOMY], isSnakeCase: boolean) {
            if (!value) {
                return '';
            }
            const materialCategories = isSnakeCase
                ? 'material_categories__full_path_with_landing_pages__material_category_id'
                : this.property;

            return `{ ${materialCategories}: ${this.format(value)} }`;
        },
        format: Number,
        property: 'materialCategories__fullPathWithLandingPages__materialCategoryId'
    },
    [SEARCH_KEYS.FOLDER]: {
        formFilterString(value: SearchState[SEARCH_KEYS.FOLDER]) {
            if (!value) {
                return '';
            }

            return `{ ${this.property}: ${this.format(value)} }`;
        },
        format: Number,
        property: 'folders'
    },
    [SEARCH_KEYS.PRICE]: {
        formFilterString(list: SearchState[SEARCH_KEYS.PRICE]) {
            if (!list?.length) {
                return '';
            }

            let initialString = '{ or: [';

            list?.forEach((id) => {
                const selectedPriceId = Number(id);

                if (selectedPriceId === PRICE_FILTER_IDS.FREE) {
                    initialString += `{ ${this.property}: 0 }, `;
                }

                if (selectedPriceId === PRICE_FILTER_IDS.LESS_THAN_2) {
                    initialString += `{ lte: { ${this.property}: 2 } }, `;
                }

                if (selectedPriceId === PRICE_FILTER_IDS.FROM_2_TO_5) {
                    initialString += `{ and: { gte: { ${this.property}: 2 }, lt: { ${this.property}: 5 } } }, `;
                }

                if (selectedPriceId === PRICE_FILTER_IDS.MORE_THAN_5) {
                    initialString += `{ gte: { ${this.property}: 5 } }, `;
                }
            });

            return `${initialString}] }`;
        },
        format: Number,
        property: 'price'
    },
    [SEARCH_KEYS.SALE]: {
        formFilterString(list: SearchState[SEARCH_KEYS.SALE], isSnakeCase: boolean) {
            const currentDate = new Date();

            const year = currentDate.getFullYear();
            const month = currentDate.getMonth() + 1;
            const day = currentDate.getDate();

            if (!list?.length) {
                return '';
            }
            const sale = isSnakeCase ? 'sale__expire_at' : this.property;

            return `{ gte: { ${sale}: "${year}-${twoDigitsFormat(month)}-${twoDigitsFormat(day)}" } }`;
        },
        format: Number,
        property: 'sale__expireAt'
    },
    [SEARCH_KEYS.LEARNING_YEARS]: {
        formFilterString(list: SearchState[SEARCH_KEYS.LEARNING_YEARS], isSnakeCase: boolean) {
            if (!list?.length) {
                return '';
            }

            let initialString = '{ or: [';
            const learningYears = isSnakeCase ? 'learning_years__id' : this.property;

            list?.forEach((id) => {
                initialString += `{ ${learningYears}: ${this.format(id)} }, `;
            });

            return `${initialString}] }`;
        },
        format: Number,
        property: 'learningYears__id'
    },
    [SEARCH_KEYS.CLASS_GRADES]: {
        formFilterString(list: SearchState[SEARCH_KEYS.CLASS_GRADES], isSnakeCase: boolean) {
            if (!list?.length) {
                return '';
            }

            let initialString = '{ or: [';
            const classGrades = isSnakeCase ? 'material_class_grades__id' : this.property;

            list?.forEach((id) => {
                initialString += `{ ${classGrades}: ${this.format(id)} }, `;
            });

            return `${initialString}] }`;
        },
        format: Number,
        property: 'materialClassGrades__id'
    },
    [SEARCH_KEYS.MATERIAL_TYPES]: {
        formFilterString(list: SearchState[SEARCH_KEYS.MATERIAL_TYPES], isSnakeCase: boolean) {
            if (!list?.length) {
                return '';
            }

            let initialString = '{ or: [';
            const materialTypes = isSnakeCase ? 'material_types__id' : this.property;

            list?.forEach((id) => {
                initialString += `{ ${materialTypes}: ${this.format(id)} }, `;
            });

            return `${initialString}] }`;
        },
        format: Number,
        property: 'materialTypes__id'
    },
    [SEARCH_KEYS.FILE_TYPES]: {
        formFilterString(list: SearchState[SEARCH_KEYS.FILE_TYPES], isSnakeCase: boolean) {
            if (!list?.length) {
                return '';
            }

            let initialString = '{ or: [';
            const fileTypes = isSnakeCase ? 'file_types' : this.property;

            list?.forEach((id) => {
                initialString += `{ ${fileTypes}: ${this.format(id)} }, `;
            });

            return `${initialString}] }`;
        },
        format: (value: string) => `"${value}"`,
        property: 'fileTypes'
    },
    [SEARCH_KEYS.TAGS]: {
        formFilterString(list: SearchState[SEARCH_KEYS.TAGS]) {
            if (!list?.length) {
                return '';
            }

            let initialString = '{ or: [';

            list?.forEach((id) => {
                initialString += `{ ${this.property}: ${this.format(id)} }, `;
            });

            return `${initialString}] }`;
        },
        format: (value: string) => `"${value}"`,
        property: 'tags'
    },
    [SEARCH_KEYS.TOTAL_PAGES]: {
        formFilterString(list: SearchState[SEARCH_KEYS.TOTAL_PAGES], isSnakeCase: boolean) {
            if (!list?.length) {
                return '';
            }

            let initialString = '{ or: [';
            const totalPages = isSnakeCase ? 'total_pages' : this.property;

            list?.forEach((id) => {
                const selectedTotalPagesId = Number(id);

                if (selectedTotalPagesId === TOTAL_PAGES_IDS.LESS_THAN_5) {
                    initialString += `{ lte: { ${totalPages}: 5 } }, `;
                }

                if (selectedTotalPagesId === TOTAL_PAGES_IDS.FROM_5_TO_20) {
                    initialString += `{ and: { gte: { ${totalPages}: 5 }, lt: { ${totalPages}: 20 } } }, `;
                }

                if (selectedTotalPagesId === TOTAL_PAGES_IDS.MORE_THAN_20) {
                    initialString += `{ gte: { ${totalPages}: 20 } }, `;
                }
            });

            return `${initialString}] }`;
        },
        format: Number,
        property: 'totalPages'
    },
    [SEARCH_KEYS.QUALITY]: {
        formFilterString(value: SearchState[SEARCH_KEYS.QUALITY], isSnakeCase: boolean) {
            if (!value?.length) {
                return '';
            }

            const highQuality = isSnakeCase ? 'is_high_quality' : this.property;

            return `{ ${highQuality}: true }`;
        },
        property: 'isHighQuality'
    },
    [SEARCH_KEYS.FORMAT]: {
        formFilterString(list: SearchState[SEARCH_KEYS.FORMAT], isSnakeCase: boolean) {
            const neutralizingSelection =
                list?.includes(`${FORMAT_FILTER_IDS.INTERACTIVE}`) &&
                list?.includes(`${FORMAT_FILTER_IDS.DOWNLOADABLE}`);
            const isStandalone = list?.includes(`${FORMAT_FILTER_IDS.INTERACTIVE_STANDALONE}`);
            const isHybrid = list?.includes(`${FORMAT_FILTER_IDS.INTERACTIVE_HYBRID}`);

            const materialFiles = isSnakeCase ? 'material_files__type' : 'materialFiles__type';
            const isBundleInteractive = isSnakeCase ? 'is_bundle_interactive' : 'isBundleInteractive';
            const interactiveId = isSnakeCase ? 'interactive_id' : 'interactiveId';
            const isUnitPublishable = isSnakeCase ? 'is_unit_publishable' : 'isUnitPublishable';
            const isStandaloneInteractive = isSnakeCase ? 'is_standalone_interactive' : 'isStandaloneInteractive';

            if (!list?.length || neutralizingSelection) {
                return '';
            }

            let initialString = '{ and: [';

            list.forEach((id) => {
                const selectedFormatId = Number(id);

                if (selectedFormatId === FORMAT_FILTER_IDS.DOWNLOADABLE) {
                    initialString += `{ ${materialFiles}: "material_file" }, `;
                    initialString += `{ ${interactiveId}: null }, `;
                    initialString += `{ not: { ${isBundleInteractive}: true } }, `;
                    initialString += `{ not: { ${materialFiles}: "interactive_preview" } }, `;
                }

                if (selectedFormatId === FORMAT_FILTER_IDS.INTERACTIVE) {
                    if (isHybrid || isStandalone) {
                        initialString += `{ ${isStandaloneInteractive}: ${isStandalone} }\n`;
                    }

                    if (isStandalone) {
                        initialString += `{ not: { ${interactiveId}: null } }`;
                    } else {
                        initialString += `{ or: [{${isBundleInteractive}: true }\n { not: {${interactiveId}: null }}] }, `;
                    }
                }
            });

            initialString += '] }';
            initialString += '{ and: { or: [';

            list.forEach((id) => {
                const selectedFormatId = Number(id);
                const userId = window.storage.getItem(storageKeys.user.userId);

                if (selectedFormatId === FORMAT_FILTER_IDS.INTERACTIVE_SELF_CREATED) {
                    initialString += `{ author__id: ${userId}, ${isUnitPublishable}: true }, `;
                }

                if (selectedFormatId === FORMAT_FILTER_IDS.INTERACTIVE_PURCHASED_CUSTOMIZED) {
                    initialString += `{ author__id: ${userId}, ${isUnitPublishable}: false }`;
                }

                if (selectedFormatId === FORMAT_FILTER_IDS.INTERACTIVE_PURCHASED) {
                    initialString += `{ not: { author__id: ${userId} } }`;
                }
            });

            initialString += '] } }';

            return initialString;
        }
    },
    [SEARCH_KEYS.OWNED_BY]: {
        formFilterString(list: SearchState[SEARCH_KEYS.OWNED_BY], isSnakeCase: boolean) {
            const neutralizingSelection = list?.includes(`${OWNED_BY.ME}`) && list?.includes(`${OWNED_BY.SCHOOL}`);

            if (!list?.length || neutralizingSelection) {
                return '';
            }

            const schoolId = isSnakeCase ? 'school_id' : 'schoolId';
            let initialString = '{';

            list.forEach((id) => {
                const selectedId = Number(id);

                if (selectedId === OWNED_BY.ME) {
                    initialString += `${schoolId}: null, `;
                }

                if (selectedId === OWNED_BY.SCHOOL) {
                    initialString += `not: { ${schoolId}: null }, `;
                }
            });

            initialString += '}';

            return initialString;
        }
    },
    [SEARCH_KEYS.QUERY]: {
        formFilterString(query: string) {
            const escapedUserInput = query?.replace(/\\/g, '').replace(/"/g, '');
            const searchByStringQuery = `
                { title: "%${escapedUserInput}%" }
            `;

            if (!escapedUserInput) {
                return '';
            }

            if (Number(escapedUserInput) > 0) {
                return `{ or: [
                    { id: ${Number(escapedUserInput)} },
                    ${searchByStringQuery}
                ] }`;
            }

            return `{ or: [
                    ${searchByStringQuery}
                ] }`;
        },
        format: (value: string) => `"%${value}%"`
    }
};

export const searchStateToGqlFilters = (search: SearchState, extra = '', isSnakeCase = false) => {
    let basicFilters = '';

    Object.keys(GQLMap).forEach((key) => {
        if (basicFilters.length) {
            basicFilters += ', ';
        }

        basicFilters += GQLMap[key].formFilterString(search[key], isSnakeCase);
    });

    return `
        filter: {
            and: [
                { world: "${world}" }
                ${basicFilters}
                ${extra}                
            ]
        }
    `;
};
